import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Button } from '~/components/ui';

function parseDateInput(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

type ShippingInfoProps = {
  onConfirm: (
    shipDate: Date,
    shipStartDate: Date,
    shipEndDate: Date | null
  ) => void;
  onCancel: () => void;
  initialShippedDate?: Date;
  initialDeliveryStartDate?: Date;
  initialDeliveryEndDate?: Date | null;
  isShipping?: boolean;
};

export function ShippingInfo({
  onConfirm,
  onCancel,
  initialShippedDate,
  initialDeliveryStartDate,
  initialDeliveryEndDate,
  isShipping = false,
}: ShippingInfoProps) {
  const { t } = useTranslation('order');
  const today = new Date();
  const [shipDate, setShipDate] = useState<Date>(initialShippedDate ?? today);
  const [shipStartDate, setShipStartDate] = useState<Date>(
    initialDeliveryStartDate ?? today
  );
  const [shipEndDate, setShipEndDate] = useState<Date | null>(
    initialDeliveryEndDate !== undefined ? initialDeliveryEndDate : today
  );

  const confirmDisabled =
    shipStartDate < shipDate ||
    (shipEndDate !== null && shipEndDate < shipStartDate);
  return (
    <div className="bg-neut-bg-alt rounded-lg  w-md p-6">
      <h3 className="text-xl text-neut-prim font-semibold mb-4">
        {t('order.enter_shipping_information')}
      </h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('order.shipped_date')} *
          </label>
          <div className="relative">
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim"
              value={toDateInputValue(shipDate)}
              onChange={(e) => {
                if (e.target.value && e.target.value !== '') {
                  setShipDate(parseDateInput(e.target.value));
                }
              }}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('order.estimated_delivery_start')} *
          </label>
          <div className="relative">
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim"
              value={toDateInputValue(shipStartDate)}
              onChange={(e) => {
                if (e.target.value && e.target.value !== '') {
                  setShipStartDate(parseDateInput(e.target.value));
                }
              }}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('order.estimated_delivery_end')}
          </label>
          <div className="relative">
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim"
              value={shipEndDate ? toDateInputValue(shipEndDate) : ''}
              onChange={(e) =>
                setShipEndDate(
                  e.target.value ? parseDateInput(e.target.value) : null
                )
              }
            />
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(shipDate, shipStartDate, shipEndDate)}
            disabled={confirmDisabled || isShipping}
            className="bg-prim"
          >
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}
